import React, { useEffect, useState } from "react";
import Container from "./Container";
import { AnimatePresence, motion } from "framer-motion";
import Button, { buttonClasses } from "./form/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Link, useLocation } from "react-router-dom";

import Logo from "./icons/Logo";
import ModeSwitch from "./ModeSwitch";

export default function Header() {
    const location = useLocation();

    const navLinkClasses = "hover:opacity-80 font-medium transition-all transform active:scale-[0.85]";
    const [smallMenuOpen, setSmallMenuOpen] = useState(false);

    useEffect(() => {
        setSmallMenuOpen(false);
    }, [location.pathname]);
    return (
        <header className="relative left-0 top-2 z-10 md:sticky">
            <Container variant={"large"}>
                <div className="flex h-24 w-full items-center justify-between gap-1 rounded-2xl border border-slate-100 bg-white/70 px-8 text-slate-600 backdrop-blur-lg dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300">
                    <Link to="/">
                        <Logo />
                    </Link>
                    <div className="flex w-fit items-center gap-4">
                        <div className="hidden items-center gap-6 md:flex">
                            <Link to="/about" className={navLinkClasses}>
                                ABOUT
                                <AnimatePresence>
                                    <motion.hr animate={{ width: location.pathname.includes("/about") ? "100%" : 0 }} className={`w-0 rounded border-t-2 border-orange-400 dark:border-orange-400`} />
                                </AnimatePresence>
                            </Link>
                            <Link to="/portfolio" className={navLinkClasses}>
                                PORTFOLIO
                                <AnimatePresence>
                                    <motion.hr animate={{ width: location.pathname.includes("/portfolio") ? "100%" : 0 }} className={`w-0 rounded border-t-2 border-emerald-400 dark:border-emerald-400`} />
                                </AnimatePresence>
                            </Link>
                            <Link to="/blog" className={navLinkClasses}>
                                BLOG
                                <AnimatePresence>
                                    <motion.hr animate={{ width: location.pathname.includes("/blog") ? "100%" : 0 }} className={`w-0 rounded border-t-2 border-indigo-400 dark:border-indigo-400`} />
                                </AnimatePresence>
                            </Link>
                            <Link to="/contact" className={navLinkClasses}>
                                CONTACT
                                <AnimatePresence>
                                    <motion.hr animate={{ width: location.pathname.includes("/contact") ? "100%" : 0 }} className={`w-0 rounded border-t-2 border-blue-400 dark:border-blue-400`} />
                                </AnimatePresence>
                            </Link>
                        </div>
                        <ModeSwitch />

                        <Popover open={smallMenuOpen} onOpenChange={setSmallMenuOpen}>
                            <PopoverTrigger className="flex items-center md:hidden">
                                <Button variant={"ghost"} size="ghost">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                                    </svg>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-64 bg-white dark:bg-slate-900">
                                <div className="flex flex-col gap-2">
                                    <Link to="/about" className={buttonClasses({ variant: "suttle", textAlign: "left", size: "small" })}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                        </svg>
                                        ABOUT
                                    </Link>
                                    <Link to="/portfolio" className={buttonClasses({ variant: "suttle", textAlign: "left", size: "small" })}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
                                            />
                                        </svg>
                                        PORTFOLIO
                                    </Link>
                                    <Link to="/blog" className={buttonClasses({ variant: "suttle", textAlign: "left", size: "small" })}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46"
                                            />
                                        </svg>
                                        BLOG
                                    </Link>
                                    <Link to="/contact" className={buttonClasses({ variant: "suttle", textAlign: "left", size: "small" })}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                            <path strokeLinecap="round" d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 10-2.636 6.364M16.5 12V8.25" />
                                        </svg>
                                        CONTACT
                                    </Link>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </Container>
        </header>
    );
}
